/**
 * ⛔⛔ WHAT DID THE SERVER ACTUALLY EVIDENCE — ACCEPTANCE, OR COMPLETION?
 *
 * The regeneration endpoints default to ASYNC and answer **202** with `{ async: true, jobId }`,
 * meaning the work is QUEUED and nothing has been written yet. Two admin surfaces read that as a
 * finished job: EnrichmentReview alerted "AEO regenerated successfully!", and QualityAudit carried
 * an `else if (data.success)` branch that refreshed the lists and cleared the spinner for a
 * response carrying no job at all. ⇒ A queued job, a job that dies in the worker, and a job that
 * finished perfectly were indistinguishable from the operator's chair.
 *
 * The decision lives here ONCE rather than in each handler, because the same decision written twice
 * gets updated once — and both of those surfaces were already a second copy of each other.
 *
 * ⚠️ THE WORDING IS DERIVED FROM THE RESPONSE, NEVER HARDCODED. Hardcoding "queued" would install
 * the mirror lie on a delay fuse: the moment a caller sends `async: false`, a genuinely completed
 * regeneration would be reported as merely queued. Reading a field is not plumbing a poller.
 */
export type RegenerationOutcome =
  /** The request was rejected. Nothing was queued and nothing ran. */
  | { kind: 'failed'; message: string }
  /** 202: accepted AND trackable. Work is queued; nothing is written yet. */
  | { kind: 'queued'; jobId: string }
  /** Accepted, but the server returned no job — so progress cannot be confirmed from here. */
  | { kind: 'accepted-untracked' }
  /** Synchronous completion: the write is done and a refresh will show real state. */
  | { kind: 'completed' };

export function classifyRegenerationResponse(
  ok: boolean,
  status: number,
  body: { success?: unknown; error?: unknown; async?: unknown; jobId?: unknown } | null
): RegenerationOutcome {
  // ⛔ `body.success` alone cannot tell a rejected request from an accepted one — neither surface
  // checked `ok`, and a proxy 502 returning HTML makes `.json()` fail, yielding a null body.
  if (!ok || !body?.success) {
    const message =
      typeof body?.error === 'string' && body.error
        ? body.error
        : `Regeneration failed (HTTP ${status})`;
    return { kind: 'failed', message };
  }

  // ⛔⛔ A TRACKABLE JOB IS THE DISCRIMINATOR, NOT THE `async` FLAG — AND THE FIRST VERSION OF THIS
  // FUNCTION GOT THAT WRONG IN A WAY THAT BROKE THE SURFACE THAT ALREADY WORKED.
  // Requiring `body.async` imported the content-review contract into the quality-audit caller,
  // which returns 200 with `{ success, jobId, statusUrl }` and NO `async` field at all. That
  // classified every successful queue as 'completed', so QualityAuditDashboard stopped calling
  // pollJobStatus and showed an error on its normal successful path.
  // 🔑 A CONSOLIDATION ASSUMES THE CALLERS SHARE A CONTRACT. THESE TWO DID NOT — and the shared
  // function looked like the fix while silently importing one caller's shape into the other.
  //
  // ⚠️ THE MIRROR WAS CHECKED BY READING BOTH ROUTES, NOT INFERRED FROM THE HANDLERS, because
  // "jobId ⇒ queued" would report FINISHED work as queued if a completed response ever carried an
  // id. Measured at origin/next:
  //   · content-review async  → 202 { success, async: true,  jobId }
  //   · content-review sync   → 200 { success, async: false, message, generationId }  ← NO jobId
  //   · quality-audit         → 200 { success, jobId, status: 'pending_verification' } ← no async
  // ⇒ NO COMPLETED RESPONSE IN EITHER ROUTE CARRIES A jobId, so the discriminator is safe for both
  // contracts. If a future route returns an id for a finished operation, THIS is the line that has
  // to change, and the test named for that shape is the one that will fail.
  if (typeof body.jobId === 'string' && body.jobId) {
    return { kind: 'queued', jobId: body.jobId };
  }

  // Positive evidence of a synchronous completion — the sync route states `async: false` outright.
  if (body.async === false) return { kind: 'completed' };

  // ⛔ Anything else is an acceptance nobody can follow: async with no usable job, or a shape
  // neither route produces. NEVER report completion without evidence of it — under-claiming is the
  // honest failure direction, over-claiming is the defect this file exists to remove.
  return { kind: 'accepted-untracked' };
}
